import { Component, OnInit, inject } from '@angular/core';

import { take } from 'rxjs';

import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';

import { EventCRUDService, EventCheckInUserOutput, EventReadOnlySchema, EventUserCreateSchema } from '@digitalforest/ngx-oak-api';

import { BaseScanPageComponent } from '../base-scan-page/base-scan-page.component';
import { ERROR_MESSAGES } from '../error-constants';

import { ZXingScannerModule } from '@zxing/ngx-scanner';

@Component({
  selector: 'app-event-scan-page',
  templateUrl: './event-scan-page.component.html',
  styleUrls: ['./event-scan-page.component.scss'],
  standalone: true,
  imports: [BadgeModule, ButtonModule, ProgressBarModule, ZXingScannerModule],
})
export class EventScanPageComponent extends BaseScanPageComponent implements OnInit {
  checkInOutput: EventCheckInUserOutput | null = null;
  badgeValue = '';
  badgeSeverity: 'success' | 'danger' | 'warning' = 'success';
  checkInPayload = '';
  private userId: number | null = null;
  private readonly eventId: number;
  eventData: EventReadOnlySchema | null = null;
  private eventCRUDService = inject(EventCRUDService);

  constructor() {
    super();
    this.eventId = parseInt(this.route.snapshot.paramMap.get('event_id') || '0');
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    // Load event data with counts
    this.eventCRUDService
      .manageGetEvent(this.eventId, this.companyId)
      .pipe(take(1))
      .subscribe({
        next: (value) => {
          this.eventData = value;
        },
        error: (error) => {
          this.logger.error('[Event] Failed to get event data', error);
          this.eventData = null;
        },
      });
  }

  protected handleScan(payload: string): void {
    this.checkInPayload = payload;
    this.userService
      .eventCheckIn(this.company!.id, this.eventId, payload)
      .pipe(take(1))
      .subscribe({
        next: (value) => {
          this.logger.debug('[Event Check-In] Success response', value);
          this.scannerEnabled = false;
          this.failureCount = 0;

          if (value.status === 'error') {
            this.errorMessage = this.getErrorMessage(value);
            this.checkInOutput = null;
            this.badgeValue = 'Error';
            this.badgeSeverity = 'danger';
          } else {
            this.errorMessage = null;
            this.checkInOutput = value.data!;

            // Check if user is not in event using the new flag
            if (value.data?.user_not_in_event) {
              this.badgeValue = 'Not Enrolled';
              this.badgeSeverity = 'warning';
              this.userId = value.data.user.id;
            } else {
              this.badgeValue = 'Success';
              this.badgeSeverity = 'success';
            }

            this.updateGroupInformation(value.data!.group_id_list);
          }

          this.updateCheckInCount();
        },
        error: (error) => {
          this.logger.error('[Event Check-In] Failed to process check-in', error);
          this.scannerEnabled = false;
          this.errorMessage = 'Failed to connect to server. Please try again.';
          this.checkInOutput = null;
          this.operationInProgress = false;
        },
        complete: () => {
          this.operationInProgress = false;
        },
      });
  }

  protected setupTestData(): void {
    this.checkInOutput = {
      user: {
        name: 'John',
        surname: 'Doe',
        corporation: 'Test Corporation',
        job_title: 'Software Engineer',
        id: 1,
        company_id: 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      event_already_checked_in: false,
      users_in_event_count: 100,
      checked_in_users_in_event_count: 42,
      user_not_in_event: false,
      group_id_list: null,
      event_id_list: null,
      agenda_id_list: null,
    };

    this.eventData = {
      id: this.eventId,
      company_id: this.companyId,
      title: 'Test Event',
      users_in_event_count: 100,
      checked_in_users_in_event_count: 42,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      sort_order: 0,
      sort_weight: 0,
    };

    this.groupInformation = [{ name: 'VIP Guests' }, { name: 'Speakers' }, { name: 'Workshop Attendees' }];

    this.adminCheckInCount = 25;
    this.operationInProgress = false;
  }

  private getErrorMessage(response: { meta?: Record<string, unknown> | null; message?: string | null }): string {
    const errorCode = response.meta?.['error_code'];

    if (errorCode && ERROR_MESSAGES[errorCode as keyof typeof ERROR_MESSAGES]) {
      return ERROR_MESSAGES[errorCode as keyof typeof ERROR_MESSAGES];
    }

    return response.message || 'An error occurred';
  }

  onAuthorize(): void {
    if (this.userId) {
      this.operationInProgress = true;
      const eventUserCreateSchema: EventUserCreateSchema = {
        user_id: this.userId,
        event_id: this.eventId,
        checked_in: true,
      };

      this.eventUserCRUDService
        .manageCreateEventUser(this.company!.id, eventUserCreateSchema)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.logger.info('[Event Authorize] Successfully authorized user for event');
            // Update UI to show success
            if (this.checkInOutput) {
              this.checkInOutput.user_not_in_event = false;
            }
            this.errorMessage = null;
            this.badgeValue = 'Success';
            this.badgeSeverity = 'success';
            this.operationInProgress = false;
            this.updateCheckInCount();
          },
          error: (error) => {
            this.logger.error('[Event Authorize] Failed to authorize user', error);
            // Update UI to show error
            this.errorMessage = 'Failed to authorize user for event';
            this.badgeValue = 'Error';
            this.badgeSeverity = 'danger';
            this.operationInProgress = false;
          },
        });
    } else {
      this.logger.error('[Event Authorize] No user ID available for authorization');
      this.errorMessage = 'Unable to authorize - user information not available';
      this.badgeValue = 'Error';
      this.badgeSeverity = 'danger';
    }
  }
}
